/** Оптимальная частота для Google STT (меньший файл → быстрее загрузка и распознавание). */
export const SPEECH_TARGET_SAMPLE_RATE = 16_000;

/** Битрейт WebM Opus для голоса (ниже дефолта браузера). */
const WEBM_VOICE_BITRATE = 32_000;

export type VoiceRecordingBackend = 'media-recorder' | 'wav';

function pickWebmMime(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = ['audio/webm;codecs=opus', 'audio/webm'];
  for (const m of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch {
      /* часть WebView бросает на неизвестный MIME */
    }
  }
  return '';
}

/** MIME для MediaRecorder — только WebM Opus (совместимо с Google STT на бэкенде). */
export function pickMediaRecorderMime(): string {
  return pickWebmMime();
}

function getAudioContextCtor(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/** Есть ли API доступа к микрофону (mediaDevices, старый navigator.getUserMedia или webkit/moz). */
export function hasGetUserMediaCapability(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.mediaDevices?.getUserMedia === 'function') return true;
  const n = navigator as Navigator & {
    getUserMedia?: unknown;
    webkitGetUserMedia?: unknown;
    mozGetUserMedia?: unknown;
  };
  return (
    typeof n.getUserMedia === 'function' ||
    !!n.webkitGetUserMedia ||
    !!n.mozGetUserMedia
  );
}

/** Запрос потока микрофона с поддержкой legacy webkit (старые мобильные Safari). */
export async function getAudioMediaStream(): Promise<MediaStream> {
  if (typeof navigator === 'undefined') {
    throw new Error('Нет navigator');
  }
  const constraints: MediaStreamConstraints = { audio: true };
  if (typeof navigator.mediaDevices?.getUserMedia === 'function') {
    return navigator.mediaDevices.getUserMedia(constraints);
  }
  type LegacyFn = (
    c: MediaStreamConstraints,
    ok: (s: MediaStream) => void,
    err: (e: unknown) => void
  ) => void;
  const legacyNav = navigator as Navigator & {
    getUserMedia?: LegacyFn;
    webkitGetUserMedia?: LegacyFn;
    mozGetUserMedia?: LegacyFn;
  };
  const legacy =
    (typeof legacyNav.getUserMedia === 'function' ? legacyNav.getUserMedia : undefined) ??
    legacyNav.webkitGetUserMedia ??
    legacyNav.mozGetUserMedia;
  if (!legacy) {
    throw new Error('getUserMedia недоступен');
  }
  return new Promise((resolve, reject) => {
    legacy.call(navigator, constraints, resolve, (e: unknown) =>
      reject(e instanceof Error ? e : new Error(String(e)))
    );
  });
}

export function pickVoiceRecordingBackend(): VoiceRecordingBackend | null {
  if (!hasGetUserMediaCapability()) return null;
  if (pickWebmMime()) return 'media-recorder';
  if (getAudioContextCtor()) return 'wav';
  return null;
}

function floatTo16BitPCM(view: DataView, offset: number, input: Float32Array): void {
  for (let i = 0; i < input.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, input[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
}

/** Линейный даунсемпл для речи (Safari отдаёт 44.1/48 kHz — STT оптимален на 16 kHz). */
function resampleLinear(samples: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return samples;
  const ratio = fromRate / toRate;
  const outLen = Math.max(1, Math.floor(samples.length / ratio));
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const srcPos = i * ratio;
    const idx = Math.floor(srcPos);
    const frac = srcPos - idx;
    const s0 = samples[idx] ?? 0;
    const s1 = samples[idx + 1] ?? s0;
    out[i] = s0 + (s1 - s0) * frac;
  }
  return out;
}

export function createVoiceMediaRecorder(stream: MediaStream, mime: string): MediaRecorder {
  return new MediaRecorder(stream, {
    mimeType: mime,
    audioBitsPerSecond: WEBM_VOICE_BITRATE,
  });
}

export function encodeFloat32ToWavBlob(samples: Float32Array, sampleRate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const writeStr = (off: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, samples.length * 2, true);
  floatTo16BitPCM(view, 44, samples);
  return new Blob([buffer], { type: 'audio/wav' });
}

export type WavCaptureHandles = {
  stop: () => void;
};

/** Запись моно WAV через ScriptProcessor (Safari / iOS, где нет WebM). */
export function startWavCapture(
  stream: MediaStream,
  onDone: (blob: Blob) => void,
  onError: (err: Error) => void
): WavCaptureHandles | null {
  const AC = getAudioContextCtor();
  if (!AC) {
    onError(new Error('AudioContext недоступен'));
    return null;
  }
  const audioContext = new AC();
  const sampleRate = audioContext.sampleRate;
  const source = audioContext.createMediaStreamSource(stream);
  const bufferSize = 4096;
  const processor = audioContext.createScriptProcessor(bufferSize, 1, 1);
  const buffers: Float32Array[] = [];

  processor.onaudioprocess = (e) => {
    const ch = e.inputBuffer.getChannelData(0);
    buffers.push(new Float32Array(ch));
  };

  source.connect(processor);
  processor.connect(audioContext.destination);

  let stopped = false;
  const finish = () => {
    if (stopped) return;
    stopped = true;
    try {
      processor.disconnect();
      source.disconnect();
      void audioContext.close();
    } catch {
      /* noop */
    }
    stream.getTracks().forEach((t) => t.stop());

    let total = 0;
    for (const b of buffers) total += b.length;
    const merged = new Float32Array(total);
    let off = 0;
    for (const b of buffers) {
      merged.set(b, off);
      off += b.length;
    }
    try {
      const prepared =
        sampleRate === SPEECH_TARGET_SAMPLE_RATE
          ? merged
          : resampleLinear(merged, sampleRate, SPEECH_TARGET_SAMPLE_RATE);
      const blob = encodeFloat32ToWavBlob(prepared, SPEECH_TARGET_SAMPLE_RATE);
      onDone(blob);
    } catch (err) {
      onError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  return {
    stop: finish,
  };
}
